import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout, Row, Col, Breadcrumb, Input, Empty } from "antd";
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import {
    HomeOutlined,
    BarChartOutlined,
    SearchOutlined,
    ArrowRightOutlined,
    UnorderedListOutlined,
    HistoryOutlined,
    ShoppingCartOutlined,
    FileDoneOutlined,
    TruckOutlined,
    SnippetsOutlined,
    DollarCircleOutlined,
    CalculatorOutlined,
    ToolOutlined,
    AccountBookOutlined,
    WalletOutlined,
    GlobalOutlined,
    FireOutlined,
} from "@ant-design/icons";
import { isPermissionAllowed } from "../../utils/permissionHelper";
import "../../styles/fluidDesign.css";

const { Content } = Layout;

// Central catalogue of the reports that already exist in the system.
// Each entry points at the existing report-generation route and reuses the
// same permission key that guards that route, so the Report Center never
// exposes a report the user cannot open. No report logic lives here - this
// page only groups and links to the existing pages.
const REPORT_CATEGORIES = [
    {
        key: "inventory",
        title: "Inventory Reports",
        description: "Stock levels, procurement and material movement",
        icon: <ShoppingCartOutlined />,
        accent: "#45a186",
        reports: [
            {
                key: "reports_inventory_items",
                title: "Inventory Report",
                description: "Stock items with category, available and minimum order quantities",
                icon: <UnorderedListOutlined />,
                path: "/reports/inventory",
                permKey: "reports_inventory",
            },
            {
                key: "reports_inventory_stockAdjustments",
                title: "Stock Adjustments Report",
                description: "Inventory stock movement adjustments with status and values",
                icon: <HistoryOutlined />,
                path: "/reports/inventory/stock-adjustments",
                permKey: "reports_inventory_stockadjustments",
            },
            {
                key: "reports_inventory_purchaseRequests",
                title: "Purchase Requests Report",
                description: "Purchase requests with supplier, item counts and amounts",
                icon: <ShoppingCartOutlined />,
                path: "/reports/inventory/purchase-requests",
                permKey: "reports_inventory_purchase_requests",
            },
            {
                key: "reports_inventory_grn",
                title: "GRN Report",
                description: "Goods received notes with supplier, PO reference and amounts",
                icon: <FileDoneOutlined />,
                path: "/reports/inventory/grn",
                permKey: "reports_inventory_grn",
            },
            {
                key: "reports_inventory_materialRequests",
                title: "Material Requests Report",
                description: "Material requests with requester, items and quantities",
                icon: <TruckOutlined />,
                path: "/reports/inventory/material-requests",
                permKey: "reports_inventory_material_requests",
            },
            {
                key: "reports_inventory_issueNotes",
                title: "Issue Notes Report",
                description: "Material issue notes with status, quantities and workflow users",
                icon: <SnippetsOutlined />,
                path: "/reports/inventory/issue-notes",
                permKey: "reports_inventory_issue_notes",
            },
            {
                key: "reports_inventory_stockValuation",
                title: "Stock Valuation Report",
                description: "Current stock quantities and values (qty x cost price)",
                icon: <DollarCircleOutlined />,
                path: "/reports/inventory/stock-valuation",
                permKey: "reports_inventory_stock_valuation",
            },
            {
                key: "reports_inventory_lowStock",
                title: "Low Stock Report",
                description: "Active items at or below their reorder level",
                icon: <CalculatorOutlined />,
                path: "/reports/inventory/low-stock",
                permKey: "reports_inventory_low_stock",
            },
        ],
    },
    {
        key: "maintenance",
        title: "Maintenance Reports",
        description: "Scheduled and completed maintenance activity",
        icon: <ToolOutlined />,
        accent: "#6a9c3f",
        reports: [
            {
                key: "reports_maintenance_tasks",
                title: "Maintenance Tasks Report",
                description: "Maintenance tasks across land, section, field and plant levels",
                icon: <SnippetsOutlined />,
                path: "/reports/maintenance/tasks",
                permKey: "reports_maintenance",
            },
        ],
    },
    {
        key: "expenses",
        title: "Expenses Reports",
        description: "Spending, payments and cash movement",
        icon: <AccountBookOutlined />,
        accent: "#d98a00",
        reports: [
            {
                key: "reports_expenses_expenses",
                title: "Expenses Report",
                description: "Expense register with category, supplier, approval and payment status",
                icon: <AccountBookOutlined />,
                path: "/reports/expenses/expenses",
                permKey: "reports_expenses",
            },
            {
                key: "reports_expenses_cashFlow",
                title: "Cash Flow Report",
                description: "Money outflow by payment account with settled, committed and rejected totals",
                icon: <DollarCircleOutlined />,
                path: "/reports/expenses/cash-flow",
                permKey: "reports_expenses_cash_flow",
            },
            {
                key: "reports_expenses_payments",
                title: "Payments Report",
                description: "Payment register with account, expense reference and settlement status",
                icon: <WalletOutlined />,
                path: "/reports/expenses/payments",
                permKey: "reports_expenses_payments",
            },
        ],
    },
    {
        key: "farmSetup",
        title: "Farm Setup Reports",
        description: "Reference registers for farm assets",
        icon: <GlobalOutlined />,
        accent: "#2f8f6f",
        reports: [
            {
                key: "reports_farm_plants",
                title: "Plants Register Report",
                description: "All registered plants with location and current status",
                icon: <FireOutlined />,
                path: "/reports/farm/plants",
                permKey: "reports_farm_plants",
            },
        ],
    },
];

const AllReports = () => {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");

    // Reports the current user is allowed to open (same keys as the routes).
    const accessibleCategories = useMemo(
        () =>
            REPORT_CATEGORIES.map((category) => ({
                ...category,
                reports: category.reports.filter((report) => isPermissionAllowed(report.permKey)),
            })).filter((category) => category.reports.length > 0),
        []
    );

    const accessibleCount = useMemo(
        () => accessibleCategories.reduce((sum, category) => sum + category.reports.length, 0),
        [accessibleCategories]
    );

    // Apply the (UI-only) search filter on top of the permission filter.
    const visibleCategories = useMemo(() => {
        const term = query.trim().toLowerCase();
        if (!term) return accessibleCategories;

        return accessibleCategories
            .map((category) => ({
                ...category,
                reports: category.reports.filter(
                    (report) =>
                        report.title.toLowerCase().includes(term) ||
                        report.description.toLowerCase().includes(term)
                ),
            }))
            .filter((category) => category.reports.length > 0);
    }, [accessibleCategories, query]);

    const visibleCount = visibleCategories.reduce((sum, category) => sum + category.reports.length, 0);

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: "/dash", title: <HomeOutlined /> },
                                { title: "Reports" },
                                { title: "All Reports" },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <BarChartOutlined /> All Reports
                        </h1>
                        <p className="fluid-page-subtitle">
                            One place to find and open every report available to you
                        </p>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="report-center-toolbar">
                    <Input
                        className="report-center-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search reports by name or description"
                        prefix={<SearchOutlined />}
                        allowClear
                    />
                    <span className="report-center-toolbar-count">
                        {visibleCount} {visibleCount === 1 ? "report" : "reports"}
                    </span>
                </div>

                {visibleCount === 0 ? (
                    <div className="fluid-card">
                        <Empty
                            description={
                                accessibleCount === 0
                                    ? "No reports are available for your account."
                                    : "No reports match your search."
                            }
                        />
                    </div>
                ) : (
                    visibleCategories.map((category) => (
                        <section className="report-center-section" key={category.key}>
                            <div className="report-center-section-head">
                                <span
                                    className="report-center-section-icon"
                                    style={{ background: `${category.accent}1f`, color: category.accent }}
                                >
                                    {category.icon}
                                </span>
                                <div className="report-center-section-text">
                                    <h2 className="report-center-section-title">{category.title}</h2>
                                    <p className="report-center-section-desc">{category.description}</p>
                                </div>
                                <span className="report-center-section-count">{category.reports.length}</span>
                            </div>

                            <div className="report-center-grid">
                                {category.reports.map((report) => (
                                    <button
                                        key={report.key}
                                        type="button"
                                        className="report-center-card"
                                        onClick={() => navigate(report.path)}
                                    >
                                        <span
                                            className="report-center-card-icon"
                                            style={{ background: `${category.accent}1f`, color: category.accent }}
                                        >
                                            {report.icon}
                                        </span>
                                        <span className="report-center-card-body">
                                            <span className="report-center-card-title">{report.title}</span>
                                            <span className="report-center-card-desc">{report.description}</span>
                                        </span>
                                        <ArrowRightOutlined className="report-center-card-arrow" />
                                    </button>
                                ))}
                            </div>
                        </section>
                    ))
                )}
            </Content>

            <Footerdiv />
        </Layout>
    );
};

export default AllReports;
